/**
 * receiptUploadApi — Firebase Storage upload for receipt images (M13a-2).
 *
 * Write-once upload of receipt images to Firebase Storage path
 * `receipts/{userId}/{receiptId}.{ext}`. Client-side MIME-allowlist and
 * size-cap guards are applied before the upload; the Storage Rules enforce
 * them server-side as well (belt + suspenders, architecture-m13.md §11).
 *
 * Key constraints (architecture-m13.md §8.4 / §9 / §10):
 *  - MIME allowlist: `image/jpeg` | `image/png` ONLY (`application/pdf` is NON-MVP).
 *  - Size cap: strictly < 10 MB (10 * 1024 * 1024 bytes).
 *  - `imageSha256` is NOT set by the client — it is computed server-side by
 *    the `extractReceipt` Function (GoBD-Integritätsanker S-1).
 *  - Cross-tenant: `userId` is always taken from `auth.currentUser.uid` (§10).
 *  - Auth fail-closed: throws if no authenticated user (Golden Rule).
 *
 * @module receiptUploadApi
 */

import { ref, uploadBytes } from 'firebase/storage';
import { collection, doc } from 'firebase/firestore';
import { storage, db, auth } from '../firebaseConfig';

// ─── Constants ────────────────────────────────────────────────────────────────

/**
 * Maximum upload size in bytes — strictly less than 10 MiB (§11, T-09).
 * The Storage Rule is `request.resource.size < 10 * 1024 * 1024`.
 */
export const MAX_RECEIPT_SIZE_BYTES = 10 * 1024 * 1024;

/** Firestore collection used to derive pre-allocated document IDs. */
const SCANNED_RECEIPTS_COLLECTION = 'scanned_receipts';

// ─── Types ────────────────────────────────────────────────────────────────────

/**
 * Result of a successful receipt image upload.
 *
 * Pass `receiptId` and `storageRef` to the `extractReceipt` Cloud Function
 * (M13a-3) to trigger asynchronous KI extraction.
 */
export interface ReceiptUploadResult {
  /**
   * Firestore document ID pre-allocated for this receipt. The `extractReceipt`
   * Function will create `scanned_receipts/{receiptId}` with this same ID.
   */
  receiptId: string;
  /**
   * Firebase Storage path of the uploaded image.
   * Format: `receipts/{userId}/{receiptId}.jpg` (or `.png` for PNG files).
   * Matches the `storageRef` field on the resulting `ScannedReceipt` document.
   */
  storageRef: string;
  /** MIME type of the uploaded image (`image/jpeg` | `image/png`). Required by extractReceipt. */
  mimeType: string;
  /** Original filename of the uploaded image (audit; optional on the server). */
  originalFilename: string;
}

// ─── Internal helpers ─────────────────────────────────────────────────────────

/**
 * Returns the UID of the currently authenticated user.
 * Throws if no user is signed in — fail-closed (Golden Rule).
 */
function requireCurrentUser(): string {
  const user = auth.currentUser;
  if (!user) {
    throw new Error(
      'receiptUploadApi: No authenticated user. Sign in before uploading receipts.',
    );
  }
  return user.uid;
}

/**
 * Maps an accepted MIME type to the canonical file extension for the storage path.
 * Defaults to `.jpg` for `image/jpeg`; uses `.png` for `image/png`.
 */
function mimeToExtension(mimeType: string): '.jpg' | '.png' {
  return mimeType === 'image/png' ? '.png' : '.jpg';
}

// ─── uploadReceiptImage ───────────────────────────────────────────────────────

/**
 * Uploads a receipt image to Firebase Storage (write-once, owner-scoped).
 *
 * Client-side guards (MIME allowlist + size cap) mirror the Storage Rules
 * for immediate, informative errors before the network round-trip. The Rules
 * enforce the same constraints server-side (T-08/T-09, architecture-m13.md §11).
 *
 * **`imageSha256` is NOT set here.** It is computed server-side by the
 * `extractReceipt` Function, which reads the uploaded bytes from Storage and
 * writes the SHA-256 hash to the `scanned_receipts` document via Admin SDK
 * (GoBD-Integritätsanker S-1). Client-SDK mutation of `imageSha256` is denied
 * by the Firestore Rules (GoBD W-1 update-allowlist).
 *
 * @param file - The image `File` from `<input type="file" accept="image/*">`.
 * @returns A {@link ReceiptUploadResult} with `receiptId` and `storageRef`
 *          to pass to the `extractReceipt` Callable (M13a-3).
 * @throws {Error} If not authenticated (fail-closed, Golden Rule).
 * @throws {Error} If MIME type is not `image/jpeg` or `image/png` (§8.4, T-08).
 * @throws {Error} If file size is >= 10 MB (T-09).
 * @throws {Error} If the Firebase Storage upload fails.
 */
export async function uploadReceiptImage(file: File): Promise<ReceiptUploadResult> {
  // Auth guard — fail-closed (Golden Rule: no unhandled auth absence).
  const uid = requireCurrentUser();

  // Client-side MIME allowlist (belt: Storage Rules enforce server-side too, T-08).
  if (file.type !== 'image/jpeg' && file.type !== 'image/png') {
    throw new Error(
      `receiptUploadApi: Unsupported MIME type "${file.type}". ` +
        'Only image/jpeg and image/png are accepted (application/pdf is NON-MVP, §8.4).',
    );
  }

  // Client-side size-cap guard (belt: Storage Rules enforce server-side too, T-09).
  if (file.size >= MAX_RECEIPT_SIZE_BYTES) {
    throw new Error(
      `receiptUploadApi: File is too large (${file.size} bytes). ` +
        'Maximum allowed size is strictly less than 10 MB (10 * 1024 * 1024 bytes).',
    );
  }

  // Pre-allocate a Firestore document ID. Used for both the Storage path and
  // the future `scanned_receipts/{receiptId}` Firestore document created by
  // the `extractReceipt` Function (architecture-m13.md §9).
  const receiptId = doc(collection(db, SCANNED_RECEIPTS_COLLECTION)).id;

  const extension = mimeToExtension(file.type);
  const storagePath = `receipts/${uid}/${receiptId}${extension}`;
  const storageRef = ref(storage, storagePath);

  // Upload write-once. The Storage Rule denies update/delete (GoBD S-2).
  // contentType is set explicitly so the Storage Rule MIME check passes.
  await uploadBytes(storageRef, file, { contentType: file.type });

  // imageSha256 is deliberately NOT set here — computed server-side by extractReceipt (S-1).
  return {
    receiptId,
    storageRef: storagePath,
    mimeType: file.type,
    originalFilename: file.name,
  };
}
